// =============================================
// Photo Upload Component
// KipiKid - העלאת תמונות עם תצוגה מקדימה (עד 5)
// =============================================

import { useRef, useState } from 'react';
import { Camera, Trash2, Upload, Plus } from 'lucide-react';
import { adminColors as colors } from '../../styles/colors';

// ===== מצב תמונה בודדת (תאימות אחורה) =====
interface SinglePhotoUploadProps {
  mode?: 'single';
  currentPhotoUrl?: string | null;
  onPhotoChange: (base64: string | null) => void;
  label?: string;
}

// ===== מצב תמונות מרובות =====
interface MultiPhotoUploadProps {
  mode: 'multi';
  currentPhotoUrls?: string[];
  maxPhotos?: number;
  onPhotosAdd: (base64List: string[]) => void;
  onPhotoRemove: (photoUrl: string) => void;
  newPhotoPreviews?: string[]; // base64 data URLs of new photos
  onNewPhotoRemove?: (index: number) => void;
  label?: string;
}

type PhotoUploadProps = SinglePhotoUploadProps | MultiPhotoUploadProps;

export default function PhotoUpload(props: PhotoUploadProps) {
  const isMulti = 'mode' in props && props.mode === 'multi';

  if (isMulti) {
    return <MultiPhotoUpload {...(props as MultiPhotoUploadProps)} />;
  }
  return <SinglePhotoUpload {...(props as SinglePhotoUploadProps)} />;
}

// ===== קומפוננטה לתמונה בודדת (תאימות אחורה) =====
function SinglePhotoUpload({ currentPhotoUrl, onPhotoChange, label = 'תמונה' }: SinglePhotoUploadProps) {
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxSizeMB = 3;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');

    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`הקובץ גדול מדי. מקסימום ${maxSizeMB}MB`);
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('יש לבחור קובץ תמונה');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      setPreview(result);
      const base64 = result.split(',')[1];
      onPhotoChange(base64);
    };
    reader.readAsDataURL(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemove = () => {
    setPreview(null);
    setError('');
    onPhotoChange(null);
  };

  const displayUrl = preview || currentPhotoUrl;

  return (
    <div>
      <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '6px', display: 'block' }}>
        {label}
      </label>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{
          width: '80px', height: '80px', borderRadius: '12px', overflow: 'hidden',
          backgroundColor: colors.background, border: `1px dashed ${colors.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {displayUrl ? (
            <img src={displayUrl} alt="תמונה" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <Camera size={24} color={colors.textLight} />
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <button type="button" onClick={() => fileInputRef.current?.click()} style={{
            padding: '8px 14px', borderRadius: '8px', border: `1px solid ${colors.primary}`,
            backgroundColor: 'white', color: colors.primary, fontSize: '13px', fontWeight: '500',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <Upload size={14} />
            {displayUrl ? 'החלף תמונה' : 'העלה תמונה'}
          </button>

          {displayUrl && (
            <button type="button" onClick={handleRemove} style={{
              padding: '8px 14px', borderRadius: '8px', border: 'none',
              backgroundColor: '#fef2f2', color: colors.error, fontSize: '13px', fontWeight: '500',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
            }}>
              <Trash2 size={14} />
              הסר תמונה
            </button>
          )}
        </div>
      </div>

      {error && <p style={{ fontSize: '12px', color: colors.error, marginTop: '6px' }}>{error}</p>}

      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} style={{ display: 'none' }} />
    </div>
  );
}

// ===== קומפוננטה לתמונות מרובות =====
function MultiPhotoUpload({
  currentPhotoUrls = [],
  maxPhotos = 5,
  onPhotosAdd,
  onPhotoRemove,
  newPhotoPreviews = [],
  onNewPhotoRemove,
  label = 'תמונות',
}: MultiPhotoUploadProps) {
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxSizeMB = 3;
  const totalPhotos = currentPhotoUrls.length + newPhotoPreviews.length;
  const canAddMore = totalPhotos < maxPhotos;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setError('');

    const remaining = maxPhotos - totalPhotos;
    if (files.length > remaining) {
      setError(`ניתן להוסיף עד ${remaining} תמונות נוספות (מקסימום ${maxPhotos})`);
    }

    const filesToProcess = Array.from(files).slice(0, remaining);
    const base64Results: string[] = [];
    let processed = 0;

    filesToProcess.forEach((file) => {
      if (file.size > maxSizeMB * 1024 * 1024) {
        setError(`הקובץ ${file.name} גדול מדי. מקסימום ${maxSizeMB}MB`);
        processed++;
        return;
      }
      if (!file.type.startsWith('image/')) {
        setError(`${file.name} אינו קובץ תמונה`);
        processed++;
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        const base64 = result.split(',')[1];
        base64Results.push(base64);
        processed++;

        if (processed === filesToProcess.length && base64Results.length > 0) {
          onPhotosAdd(base64Results);
        }
      };
      reader.readAsDataURL(file);
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '6px', display: 'block' }}>
        {label} ({totalPhotos}/{maxPhotos})
      </label>

      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px',
      }}>
        {/* תמונות קיימות */}
        {currentPhotoUrls.map((url, index) => (
          <div key={`existing-${index}`} style={{ position: 'relative' }}>
            <div style={{
              width: '80px', height: '80px', borderRadius: '10px', overflow: 'hidden',
              border: `1px solid ${colors.border}`,
            }}>
              <img src={url} alt={`תמונה ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <button type="button" onClick={() => onPhotoRemove(url)} style={{
              position: 'absolute', top: '-6px', right: '-6px', width: '22px', height: '22px',
              borderRadius: '50%', border: 'none', backgroundColor: colors.error, color: 'white',
              fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 0,
            }}>
              <Trash2 size={12} />
            </button>
          </div>
        ))}

        {/* תמונות חדשות (טרם נשמרו) */}
        {newPhotoPreviews.map((dataUrl, index) => (
          <div key={`new-${index}`} style={{ position: 'relative' }}>
            <div style={{
              width: '80px', height: '80px', borderRadius: '10px', overflow: 'hidden',
              border: `2px solid ${colors.primary}`,
            }}>
              <img src={dataUrl} alt={`תמונה חדשה ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            {onNewPhotoRemove && (
              <button type="button" onClick={() => onNewPhotoRemove(index)} style={{
                position: 'absolute', top: '-6px', right: '-6px', width: '22px', height: '22px',
                borderRadius: '50%', border: 'none', backgroundColor: colors.error, color: 'white',
                fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 0,
              }}>
                <Trash2 size={12} />
              </button>
            )}
            <div style={{
              position: 'absolute', bottom: '2px', left: '2px', right: '2px',
              backgroundColor: 'rgba(99,102,241,0.8)', color: 'white', fontSize: '9px',
              textAlign: 'center', borderRadius: '0 0 8px 8px', padding: '1px',
            }}>
              חדשה
            </div>
          </div>
        ))}

        {/* כפתור הוספה */}
        {canAddMore && (
          <button type="button" onClick={() => fileInputRef.current?.click()} style={{
            width: '80px', height: '80px', borderRadius: '10px',
            border: `2px dashed ${colors.border}`, backgroundColor: colors.background,
            cursor: 'pointer', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: '4px',
          }}>
            <Plus size={20} color={colors.textLight} />
            <span style={{ fontSize: '10px', color: colors.textLight }}>הוסף</span>
          </button>
        )}
      </div>

      {totalPhotos === 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '12px', borderRadius: '8px', backgroundColor: colors.background,
          border: `1px dashed ${colors.border}`,
        }}>
          <Camera size={20} color={colors.textLight} />
          <span style={{ fontSize: '13px', color: colors.textLight }}>אין תמונות. לחץ על + להוספה.</span>
        </div>
      )}

      {error && <p style={{ fontSize: '12px', color: colors.error, marginTop: '4px' }}>{error}</p>}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
    </div>
  );
}
